import React, { useContext, useEffect, useState } from "react";
import { Button, Spin, Tooltip } from "antd";
import { Select, message, Typography } from "antd";
import { PlusOutlined, SendOutlined, UnorderedListOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { botOptions } from "../../constants/chatPageConstants";
import { ThemeContext } from "../../contexts/themeConfig";
import UsefulPromptDropdown from "./UsefulPromptDropdown";
import { FaRegStopCircle } from "react-icons/fa";
import useRemaining from "../../Hooks/useRemaining";
import { useNavigate } from "react-router-dom";
import { getUserRole } from "../../Utility/service";
import { userSubscriptionInfo } from "../../api/profile";
const { Text } = Typography;
const SHORTCUT_PROMPT_TEXT = "Press '/' to access a list of task commands.";
const SHORTCUT_PROMPT_ERROR =
  "Please enter a prompt before selecting a command!";
const SEPARATOR_TIP = "Tip: Use exactly 4 dashes '----' as the separator to queue more than one message in one go";
const ChatPromptInputForm = ({
  states,
  actions,
  refs,
  showSelectors = true,
}) => {
  const {
    selectedTags,
    tags,
    loading = false,
    inputPrompt,
    showPromptDropdown,
    showMassage,
    setShowMassage
  } = states;
  const { promptInputRef } = refs;
  const {
    onSubmit,
    handleSelectTags,
    setSelectedTags,
    setSelectedChatModel,
    setInputPrompt,
    setShowPromptDropdown,
    handleStopGeneratingButton
  } = actions;
  const { remaining, userInfo, } = useRemaining();
  const { theme } = useContext(ThemeContext);
  const [defaultProvider, setDefaultProvider] = useState("");
  const tagOptions = tags?.map((tag) => ({
    value: tag._id,
    label: tag.title,
  }));
  const handleBotChange = (selectedBot) => {
    setSelectedChatModel(selectedBot);
  };
  const handleTagChange = (selectedValues) => {
    const updatedSelectedTags = tags.filter((tag) =>
      selectedValues.includes(tag._id)
    );
    setSelectedTags(updatedSelectedTags);
    handleSelectTags(updatedSelectedTags);
  };
  const handleKeyDown = (event) => {
    if (loading) return;
    if (event.key === "/") {
      setShowPromptDropdown(true);
    }
    if (event.key !== "/" && showPromptDropdown) {
      setShowPromptDropdown(false);
    }
    if (event.key === "Enter" && event.shiftKey) {
      setInputPrompt((prevValue) => prevValue);
    } else if (event.key === "Enter") {
      event.preventDefault();
      event.target.style.height = "51px";
      onSubmit(event);
    }
  };

  const onInputPromptChange = (event) => {
    setInputPrompt(event.target.value);
    // Show message if there are separators in the text
    setShowMassage(event.target.value.includes('----'));
  };
  const handleUsefulPromptSelection = (selectedPrompt) => {
    const lastSlashRemoved = inputPrompt.replace(/\/$/, "");
    if (!lastSlashRemoved.trim()) {
      return message.warning(SHORTCUT_PROMPT_ERROR);
    }
    if (selectedPrompt && selectedPrompt?.label) {
      let userInputtedPrompt = `${lastSlashRemoved} ${selectedPrompt.label}`;
      setInputPrompt(userInputtedPrompt);
      setShowPromptDropdown(false);
      onSubmit(null, userInputtedPrompt);
    }
  };
  const scrollUp = () => {
    const textarea = promptInputRef.current;
    if (textarea) {
      textarea.scrollBy(0, -30);
    }
  };
  const scrollDown = () => {
    const textarea = promptInputRef.current;
    if (textarea) {
      textarea.scrollBy(0, 30);
    }
  };
  const getChatProviders = () => {
    if (userInfo?.role === "superadmin") {
      return botOptions;
    };
    const chatProviders = remaining?.chat_providers;
    const accessProviders = botOptions?.map((option) =>
      chatProviders?.includes(option.value) ? option : { ...option, disabled: true }
    );

    return accessProviders;
  };

  const [activePlan, setActivePlan] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();

  const userRole = getUserRole();

  const fetchData = async () => {
    setFetching(true);
    try {
      const subscriptionRes = await userSubscriptionInfo();
      setActivePlan(subscriptionRes?.activePlan);
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  useEffect(() => {
    if (userInfo?.role === "superadmin") {
      const value = botOptions[0].value;
      setDefaultProvider(value);
      setSelectedChatModel(value);
    } else {
      const chatProviders = remaining?.chat_providers;
      const value = chatProviders?.includes("openai") ? "openai" : chatProviders?.[0] || "";
      setDefaultProvider(value);
      setSelectedChatModel(value);
    };
  }, [remaining, setSelectedChatModel, userInfo]);

  return (
    <div className="parent-container">
      <form
        className={`form-style ${theme === "dark" && "dark-mode"}`}
        onSubmit={onSubmit}
      >
        {showMassage && (
          <div className="query-massage">
            <p>
              <InfoCircleOutlined /> 
              <b className="ms-2 queue-title">{inputPrompt.split('----').filter(part => part.trim()).length} messages will be queued </b>
              <br />
              <small className="ms-4 queue-desc">{SEPARATOR_TIP}</small>
            </p>
          </div>
        )}

        <div
          className={`inputPromptTextarea-container ${theme === "dark" && "dark-mode"
            }`}
        >
          {showSelectors && (
            <div
              className={`select-container-whole ${theme === "dark" && "dark-mode"
                }`}
            >
              <div className="select-container">
                <Tooltip title="Choose an AI">
                  <Select
                    style={{ width: "30%" }}
                    placeholder="Choose Bot"
                    onChange={handleBotChange}
                    options={botOptions}
                    defaultValue={botOptions[0].value}
                    className="custom-select-bot"
                    placement="topLeft"
                  />
                </Tooltip>
                <Tooltip title="Choose a Tag">
                  <Select
                    style={{ width: "50%" }}
                    placeholder="Choose tags"
                    showSearch
                    value={selectedTags.map((tag) => tag._id)}
                    onChange={handleTagChange}
                    options={tagOptions}
                    notFoundContent={loading ? "Loading..." : null}
                    className="custom-select-tags"
                    placement="topLeft"
                  />
                </Tooltip>
              </div>
              <div className="blurry-box"></div>
            </div>
          )}
          <UsefulPromptDropdown
            isVisible={showPromptDropdown}
            onSelection={handleUsefulPromptSelection}
          >
            <>
            {fetching && <div className="loading-overlay"></div>}
            <textarea
              ref={promptInputRef}
              autoComplete="off"
              placeholder="Ask me anything..."
              name="inputPrompt"
              className={`inputPrompttTextarea ${theme === "dark" && "dark-mode"
                }`}
              rows="2"
              value={inputPrompt}
              onKeyDown={handleKeyDown}
              onChange={onInputPromptChange}
              style={{ height: "auto" }}
              onInput={(e) => {
                const textarea = e.target;
                textarea.style.height = "auto";
                textarea.style.height = `${textarea.scrollHeight}px`;
              }}
            />
            </>
          </UsefulPromptDropdown>
          <div className="actions-container">
            <Tooltip title="It will be available soon">
              <button className="plus-button" aria-label="Attach" disabled>
                <span className="plusbtn-icon"><PlusOutlined /></span>
              </button>
            </Tooltip>
            <div className="d-flex">
              <button
                disabled={loading || !inputPrompt.trim()}
                aria-label="Insert separator"
                className={`sendIcon-outline ${theme === "dark" && "dark-mode"}`}
                type="button"
                onClick={() => {
                  setInputPrompt(prev => `${prev}\n----\n`);
                  setShowMassage(true);
                }}
              >
                <UnorderedListOutlined />
              </button>
              <button
                disabled={loading || !inputPrompt.trim()}
                aria-label="form submit"
                className={`sendIcon ${theme === "dark" && "dark-mode"}`}
                type="submit"
              >
                <SendOutlined className="send" />
              </button>
            </div>
          </div>
        </div>
      </form>
      {loading ? (
        <div className="stop-generating-btn-container multi-provider-area-stop-btn">
          <Button danger type="primary" onClick={handleStopGeneratingButton} style={{ width: "160px", }} icon={<FaRegStopCircle />} size={30}>
            Stop Generating
          </Button>
        </div>
      ) : <></>}
      <div
        style={{ marginTop: "20px" }}
        className={`shortcut-text ${theme === "dark" && "dark-mode"}`}
      >
        <Text style={{ color: "#717171" }}>
          Press <span className={`slash-icon ${theme === "dark" && "dark-mode"}`}>/</span> to access a list of task commands.</Text>
      </div>
    </div>
  );
};
export default ChatPromptInputForm;
